import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../Config';

const ReparationDetailsVehicule = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [reparation, setReparation] = useState(null);
    const [demande, setDemande] = useState(null);
    const [vehicule, setVehicule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [pieceToDelete, setPieceToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const token = localStorage.getItem('token');

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                setUser(JSON.parse(userData));
            } catch (err) {
                console.error('Erreur parsing user:', err);
            }
        }
        
        fetchReparationDetails();
    }, [id]);

    const fetchReparationDetails = async () => {
        try {
            setLoading(true);
            
            const reparationRes = await axios.get(`${API_URL}/reparations/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReparation(reparationRes.data);
            
            if (reparationRes.data.demande_reparation_id) {
                await fetchDemandeDetails(reparationRes.data.demande_reparation_id);
            }
            
            setError(null);
        } catch (err) {
            setError('Erreur lors du chargement de la réparation');
        } finally {
            setLoading(false);
        }
    };

    const fetchDemandeDetails = async (demandeId) => {
        try {
            const demandeRes = await axios.get(`${API_URL}/demandes-reparations/${demandeId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDemande(demandeRes.data);
            
            if (demandeRes.data.vehicule_id) {
                const vehiculeRes = await axios.get(`${API_URL}/vehicules/${demandeRes.data.vehicule_id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setVehicule(vehiculeRes.data);
            }
        } catch (err) {
            console.error('Erreur chargement demande:', err);
        }
    };

    const confirmDeletePiece = (piece) => {
        if (!user || !['mecanicien', 'magasinier', 'validateur'].includes(user.role)) {
            return;
        }
        setPieceToDelete(piece);
        setShowDeleteAlert(true);
    };

    const handleDeletePiece = async () => {
        if (!pieceToDelete) return;
        
        setDeleteLoading(true);
        try {
            await axios.delete(`${API_URL}/pieces-remplacees/${pieceToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchReparationDetails();
            setShowDeleteAlert(false);
            setPieceToDelete(null);
        } catch (err) {
            console.error(err);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleAddPiece = () => {
        navigate(`/pieces/nouvelle?reparation_id=${id}`);
    };

    const handleEditReparation = () => {
        navigate(`/reparations/${id}/edit`);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toFixed(2) + ' MAD';
    };

    if (loading) {
        return (
            <div className="min-h-screen  flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement...</p>
                </div>
            </div>
        );
    }

    if (error || !reparation) {
        return (
            <div className="min-h-screen  flex items-center justify-center">
                <div className="max-w-md w-full">
                    <div className="bg-white p-6 rounded-lg border text-center">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Erreur</h2>
                        <p className="text-gray-600 mb-6">{error || 'Réparation non trouvée'}</p>
                        <Link
                            to="/reparations"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Retour aux réparations
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const totalPieces = reparation.pieces?.reduce((total, piece) => 
        total + (parseFloat(piece.prix || 0) * (piece.quantite || 1)), 0
    ) || 0;
    
    const montantTotal = parseFloat(reparation.montant || 0);
    const mainDOeuvre = montantTotal - totalPieces;

    const canEdit = user && (user.role === 'mecanicien' || user.role === 'validateur');
    const canAddPieces = user && (user.role === 'mecanicien' || user.role === 'magasinier');
    const canDeletePieces = user && ['mecanicien', 'magasinier', 'validateur'].includes(user.role);

    return (
        <div className="min-h-screen ">
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <Link
                            to="/reparations"
                            className="inline-flex items-center text-gray-600 hover:text-gray-900 text-sm"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Retour aux réparations
                        </Link>
                    </div>
                    
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Réparation #{reparation.id}</h1>
                            <p className="text-gray-600 mt-1">
                                {vehicule?.immatriculation || vehicule?.numero || 'Véhicule'} • {formatDate(reparation.created_at)}
                            </p>
                        </div>
                        
                        <div className="flex space-x-2">
                            {canEdit && (
                                <button
                                    onClick={handleEditReparation}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded hover:"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Modifier
                                </button>
                            )}
                            
                            {canAddPieces && (
                                <button
                                    onClick={handleAddPiece}
                                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Ajouter pièce
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Grille principale */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Colonne gauche */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Informations réparation */}
                        <div className="bg-white rounded-lg border p-6">
                            <div className="flex items-center mb-4">
                                <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <h2 className="font-semibold text-gray-900">Informations réparation</h2>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Mécanicien</p>
                                    <p className="font-medium">{reparation.mecanicien?.nom || 'Non spécifié'}</p>
                                </div>
                                
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Coût total</p>
                                    <p className="text-xl font-bold text-gray-900">{formatCurrency(montantTotal)}</p>
                                </div>
                                
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Kilométrage après</p>
                                    <p className="font-medium">{reparation.km_apres_reparation?.toLocaleString()} km</p>
                                </div>
                                
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Date</p>
                                    <p className="font-medium">{formatDate(reparation.created_at)}</p>
                                </div>
                            </div>
                            
                            {reparation.description_finale && (
                                <div className="border-t pt-4 mt-4">
                                    <p className="text-sm text-gray-500 mb-2">Description</p>
                                    <div className=" p-3 rounded">
                                        <p className="text-gray-700">{reparation.description_finale}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Pièces remplacées */}
                        <div className="bg-white rounded-lg border">
                            <div className="p-4 border-b flex items-center justify-between">
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                    <h2 className="font-semibold text-gray-900">Pièces remplacées</h2>
                                </div>
                                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                                    {reparation.pieces?.length || 0} pièce(s)
                                </span>
                            </div>
                            
                            {reparation.pieces && reparation.pieces.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Pièce</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Quantité</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Prix unitaire</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Total</th>
                                                {canDeletePieces && <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {reparation.pieces.map((piece) => {
                                                const pieceTotal = parseFloat(piece.prix || 0) * (piece.quantite || 1);
                                                return (
                                                    <tr key={piece.id} className="hover:">
                                                        <td className="px-4 py-3">
                                                            <div className="font-medium text-gray-900">{piece.nom_piece}</div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                                                                {piece.quantite || 1}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {piece.prix ? formatCurrency(piece.prix) : 'N/A'}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="font-medium text-gray-900">
                                                                {formatCurrency(pieceTotal)}
                                                            </span>
                                                        </td>
                                                        {canDeletePieces && (
                                                            <td className="px-4 py-3">
                                                                <button
                                                                    onClick={() => confirmDeletePiece(piece)}
                                                                    className="text-red-600 hover:text-red-800"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot className="">
                                            <tr>
                                                <td colSpan={canDeletePieces ? 3 : 2} className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                                                    Total pièces :
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="font-bold text-gray-900">{formatCurrency(totalPieces)}</span>
                                                </td>
                                                {canDeletePieces && <td></td>}
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            ) : (
                                <div className="p-8 text-center">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                        </svg>
                                    </div>
                                    <p className="text-gray-500 mb-3">Aucune pièce enregistrée</p>
                                    {canAddPieces && (
                                        <button
                                            onClick={handleAddPiece}
                                            className="text-blue-600 hover:text-blue-800 font-medium"
                                        >
                                            Ajouter une pièce
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Colonne droite */}
                    <div className="space-y-6">
                        {/* Véhicule */}
                        {vehicule && (
                            <div className="bg-white rounded-lg border p-6">
                                <div className="flex items-center mb-4">
                                    <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4zM4 9a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V9z" />
                                    </svg>
                                    <h3 className="font-semibold text-gray-900">Véhicule</h3>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Immatriculation</p>
                                        <p className="font-medium">{vehicule.immatriculation || vehicule.numero}</p>
                                    </div>
                                    
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Marque & Modèle</p>
                                        <p className="font-medium">{vehicule.marque} {vehicule.modele}</p>
                                    </div>
                                    
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Kilométrage</p>
                                        <p className="font-medium">{vehicule.km_actuel?.toLocaleString()} km</p>
                                    </div>
                                    
                                    <div className="pt-2">
                                        <Link
                                            to={`/vehicules/${vehicule.id}`}
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                        >
                                            Voir détails →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Demande associée */}
                        {demande && (
                            <div className="bg-white rounded-lg border p-6">
                                <div className="flex items-center mb-4">
                                    <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <h3 className="font-semibold text-gray-900">Demande associée</h3>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Type</p>
                                        <p className="font-medium">{demande.type_reparation || 'Non spécifié'}</p>
                                    </div>
                                    
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Kilométrage avant</p>
                                        <p className="font-medium">{demande.km_actuel?.toLocaleString()} km</p>
                                    </div>
                                    
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Date</p>
                                        <p className="font-medium">{formatDate(demande.created_at)}</p>
                                    </div>
                                    
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Statut</p>
                                        <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Terminé
                                        </span>
                                    </div>
                                    
                                    <div className="pt-2">
                                        <Link
                                            to={`/demandes-reparation/${demande.id}`}
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                        >
                                            Voir demande →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Résumé financier */}
                        <div className="bg-white rounded-lg border p-6">
                            <div className="flex items-center mb-4">
                                <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h3 className="font-semibold text-gray-900">Résumé financier</h3>
                            </div>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Main d'œuvre</span>
                                    <span className="font-medium">{formatCurrency(mainDOeuvre)}</span>
                                </div>
                                
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Pièces</span>
                                    <span className="font-medium">{formatCurrency(totalPieces)}</span>
                                </div>
                                
                                <div className="border-t pt-3 mt-3">
                                    <div className="flex justify-between font-bold">
                                        <span className="text-gray-900">Total</span>
                                        <span className="text-gray-900">{formatCurrency(montantTotal)}</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Graphique de répartition */}
                            {montantTotal > 0 && (
                                <div className="mt-4 pt-4 border-t">
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-xs text-gray-500">
                                            <span>Main d'œuvre</span>
                                            <span>{((mainDOeuvre / montantTotal) * 100).toFixed(0)}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-gray-700 h-2 rounded-full" 
                                                style={{ width: `${Math.max(0, (mainDOeuvre / montantTotal) * 100)}%` }}
                                            ></div>
                                        </div>
                                        
                                        <div className="flex justify-between text-xs text-gray-500">
                                            <span>Pièces</span>
                                            <span>{((totalPieces / montantTotal) * 100).toFixed(0)}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-blue-600 h-2 rounded-full" 
                                                style={{ width: `${Math.max(0, (totalPieces / montantTotal) * 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de confirmation de suppression */}
            {showDeleteAlert && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.282 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Supprimer la pièce</h3>
                        <p className="text-gray-600 text-center mb-2">Voulez-vous supprimer cette pièce ?</p>
                        <p className="text-gray-800 font-medium text-center mb-4">{pieceToDelete?.nom_piece}</p>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => {
                                    setShowDeleteAlert(false);
                                    setPieceToDelete(null);
                                }}
                                className="flex-1 py-2 border rounded text-gray-700 hover:"
                                disabled={deleteLoading}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDeletePiece}
                                disabled={deleteLoading}
                                className="flex-1 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                            >
                                {deleteLoading ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Suppression...
                                    </div>
                                ) : (
                                    'Supprimer'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReparationDetailsVehicule;